import { useEffect, useState, type FC } from "react";
import { ChamberWireframe } from "./ChamberWireframe";
import { ChamberAtmosphere } from "./ChamberAtmosphere";
import { ChamberEmptyState } from "./ChamberEmptyState";
import { BuildProgressPanel, type BuildProgressRow } from "./BuildProgressPanel";
import { BuilderFeed } from "./BuilderFeed";
import { AppCard } from "./AppCard";
import liveCopy from "../copy/live.json";
import { useLiveBuild } from "../demo/useLiveBuild";
import type { OrganizationId } from "../types";

export interface LiveBuildStageProps {
  orgId: OrganizationId;
  orgName: string;
  enabled: boolean;
  /** Increment to clear the shown run; the stage goes back to waiting until a newer run arrives. */
  resetToken?: number;
  reducedMotion?: boolean;
}

function dismissedKey(orgId: OrganizationId): string {
  return `heavy_rain_live_dismissed_${orgId}`;
}

function readDismissed(orgId: OrganizationId): string | null {
  try {
    return window.localStorage.getItem(dismissedKey(orgId));
  } catch {
    return null;
  }
}

const PHASES = ["received", "reading", "building", "checking", "ready"] as const;
type Phase = (typeof PHASES)[number];

function fill(template: string, tokens: Record<string, string>): string {
  return Object.entries(tokens).reduce((text, [key, value]) => text.split(`{${key}}`).join(value), template);
}

/** Real builds for one organization: empty until work arrives, then the panel, the builder's feed, and the app card. */
export const LiveBuildStage: FC<LiveBuildStageProps> = ({ orgId, orgName, enabled, resetToken = 0, reducedMotion = false }) => {
  const live = useLiveBuild(orgId, enabled);
  const [dismissedRunId, setDismissedRunId] = useState<string | null>(() => readDismissed(orgId));
  useEffect(() => {
    setDismissedRunId(readDismissed(orgId));
  }, [orgId]);
  useEffect(() => {
    if (resetToken === 0) return;
    const current = live.run?.runId ?? null;
    setDismissedRunId(current);
    try {
      if (current) window.localStorage.setItem(dismissedKey(orgId), current);
      else window.localStorage.removeItem(dismissedKey(orgId));
    } catch {
      // Storage may be unavailable; the reset still applies for this session.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetToken]);
  const dismissed = Boolean(live.run && live.run.runId === dismissedRunId);
  const run = dismissed ? null : live.run;
  const progress = dismissed ? [] : live.progress;
  const artifact = dismissed ? null : live.artifact;
  const active = run && run.phase !== "ready" && run.phase !== "failed";
  const ready = Boolean(run && run.phase === "ready" && artifact);
  const failed = Boolean(run && run.phase === "failed");

  // Fidelity rises with what the builder has actually written.
  const filesWritten = run?.filesWritten.length ?? 0;
  const veilStage: "queued" | "stage1" | "stage2" | "stage3" =
    !run || run.phase === "received"
      ? "queued"
      : run.phase === "reading"
        ? "stage1"
        : run.phase === "checking" || filesWritten >= 3
          ? "stage3"
          : "stage2";

  const phaseIndex = run ? PHASES.indexOf(run.phase as Phase) : -1;
  const progressFile = run?.progress ?? null;
  const subSteps = progressFile
    ? [
        ...progressFile.done.map((label, index) => ({ key: `done-${index}`, label, state: "done" as const })),
        ...(progressFile.doing ? [{ key: "doing", label: progressFile.doing, state: "active" as const }] : []),
        ...progressFile.next.slice(0, 3).map((label, index) => ({ key: `next-${index}`, label, state: "queued" as const })),
      ]
    : [];
  const rows: BuildProgressRow[] = PHASES.map((phase, index) => ({
    key: phase,
    label: liveCopy.panel.phases[phase],
    items: phase === "building" ? subSteps : undefined,
    state: failed
      ? index === 0
        ? "done"
        : index === 1
          ? "paused"
          : "queued"
      : index < phaseIndex || (ready && index === phaseIndex)
        ? "done"
        : index === phaseIndex
          ? "active"
          : "queued",
  }));

  const title = run
    ? run.phase === "received"
      ? fill(liveCopy.panel.receivedTitle, { app: run.app.name })
      : fill(liveCopy.panel.buildingTitle, { app: run.app.name })
    : "";
  const subtitle = run
    ? fill(liveCopy.panel.requestedBy, { name: run.requestedBy.name, role: run.requestedBy.role })
    : null;
  const notice = run
    ? run.status === "incomplete"
      ? { tone: "failed" as const, title: liveCopy.panel.failedTitle, body: liveCopy.panel.stoppedBody }
      : run.status === "requires_action"
        ? { tone: "info" as const, body: liveCopy.panel.waitingInputBody }
        : failed
          ? {
              tone: "failed" as const,
              title: liveCopy.panel.failedTitle,
              body: fill(liveCopy.panel.failedBody, { reason: run.errorReason ?? "" }),
            }
          : null
    : null;

  const tokens = run?.usage.totalTokens ?? 0;
  const tokensLabel = tokens > 0 ? fill(liveCopy.feed.tokens, { n: tokens.toLocaleString("en-US") }) : null;
  const filesLabel = filesWritten > 0 ? fill(liveCopy.feed.files, { n: String(filesWritten) }) : null;

  return (
    <div className="hr-workbench-c hr-workbench-c--live">
      <section className={`hr-workbench-c__chamber hr-workbench-c__chamber--${ready ? "ready" : veilStage}${!run || ready ? " hr-workbench-c__chamber--clear hr-workbench-c__chamber--light" : ""}`} aria-label={run ? run.app.name : orgName}>
        <div className="hr-chamber-stage">
          <ChamberAtmosphere variant={run && !ready ? "dark" : "pale"} reducedMotion={reducedMotion} />

          {!run && (
            <ChamberEmptyState title={liveCopy.empty.liveTitle} />
          )}

          {run && !ready && (
            <>
              {run.snapshot ? (
                <div
                  className={`hr-chamber-stage__artifact hr-chamber-stage__artifact--${veilStage} hr-chamber-snapshot`}
                  aria-hidden="true"
                >
                  <iframe
                    key={run.snapshot.version}
                    src={run.snapshot.indexUrl}
                    title=""
                    className="hr-chamber-snapshot__frame"
                    tabIndex={-1}
                    sandbox="allow-scripts"
                  />
                </div>
              ) : (
                (run.phase === "building" || run.phase === "checking") && (
                  <ChamberWireframe orgId={orgId} buildStage={veilStage} />
                )
              )}
              <div className={`hr-chamber-smoke-veil hr-chamber-smoke-veil--${veilStage}`} aria-hidden="true" />
              <BuilderFeed
                events={progress}
                opening={{
                  title: liveCopy.feed.handoffTitle,
                  detail: fill(liveCopy.feed.handoffDetail, {
                    name: run.requestedBy.name,
                    app: run.app.name,
                    model: run.model,
                  }),
                }}
                tokensLabel={tokensLabel}
                filesLabel={filesLabel}
                reducedMotion={reducedMotion}
              />
              <BuildProgressPanel
                title={title}
                subtitle={subtitle}
                rows={rows}
                notice={notice}
                reducedMotion={reducedMotion}
              />
            </>
          )}

          {run && ready && artifact && (
            <AppCard
              artifact={artifact}
              appName={run.app.name}
              purpose={run.app.purpose}
              copy={{ open: liveCopy.card.open }}
            />
          )}
        </div>
      </section>
    </div>
  );
};
